/**
 * WelcomeOfferBanner — tells signed-out visitors about the loyalty program's
 * first-order earn rule (e.g. "Get 100% back on your first order"). Hidden
 * when the tenant has no such rule or the lookup fails.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { loyaltyApi, type WelcomeOffer } from "@/api/services";
import { Icons } from "@/components/Icons";
import { Price } from "@/components/Price";
import { Card } from "@/components/ui";

export function WelcomeOfferBanner() {
  const { t } = useTranslation();
  const [offer, setOffer] = useState<WelcomeOffer | null>(null);
  const [programType, setProgramType] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loyaltyApi
      .welcomeOffer()
      .then((res) => {
        if (cancelled) return;
        setOffer(res?.welcomeOffer ?? null);
        setProgramType(res?.programType ?? null);
      })
      .catch(() => {
        /* marketing banner only */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!offer || offer.rate <= 0) return null;
  const isPoints = programType === "points";

  return (
    <Card padding={14} style={{ display: "flex", alignItems: "center", gap: 12, margin: "0 16px 12px" }}>
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: "var(--primary-50)",
          display: "grid",
          placeItems: "center",
          color: "var(--primary-700)",
        }}
      >
        <Icons.spark size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="display" style={{ fontSize: 14, fontWeight: 700 }}>
          {isPoints
            ? t("v2.loyalty.welcome_points", {
                defaultValue: "Earn {{rate}} points per unit on your first order",
                rate: offer.rate,
              })
            : t("v2.loyalty.welcome_cashback", {
                defaultValue: "Get {{rate}}% back on your first order",
                rate: offer.rate,
              })}
        </div>
        {offer.maxEarnPerOrder ? (
          <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 2 }}>
            {t("v2.loyalty.welcome_cap", { defaultValue: "Up to" })}{" "}
            {isPoints ? `${offer.maxEarnPerOrder}` : <Price amount={offer.maxEarnPerOrder} />}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
